<template>
  <div class="wrapper usher-page-new">
    <div class="usher-page-new__shell">
      <section class="usher-hero">
        <div class="usher-hero__inner">
          <div class="usher-hero__meta">
            <div style="width:100%;">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%;">
                <p class="usher-hero__eyebrow" style="margin:0;">{{ currentDate() }}</p>
                <div style="display:flex;align-items:center;justify-content:flex-end;gap:10px;flex:0 0 auto;white-space:nowrap;">
                  <div style="font-size:14px;font-weight:700;color:#7c5a18;">Name: {{ operatorName || 'Not set' }}</div>
                  <button
                    type="button"
                    style="display:inline-flex;align-items:center;justify-content:center;border:1px solid #d7b46a;background:#fffaf0;color:#7c5a18;border-radius:999px;width:38px;height:38px;padding:0;cursor:pointer;flex:0 0 auto;"
                    aria-label="Update operator name"
                    title="Update name"
                    @click="openOperatorPrompt"
                  >
                    <CIcon name="cil-pencil" height="16" />
                  </button>
                </div>
              </div>
              <div style="display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin-top:4px;">
                <h2 class="usher-hero__title">Usher Dashboard</h2>
                <div class="usher-hero__total">Queue Total: {{ meta.total }}</div>
              </div>
            </div>
          </div>

          <form class="usher-search" @submit.prevent="submitSearch">
            <div class="usher-search__field">
              <input
                v-model.trim="searchInput"
                type="text"
                class="usher-search__input"
                placeholder="Search by child's name..."
                @keyup.enter.prevent="submitSearch"
              >
              <button
                v-if="searchInput"
                type="button"
                class="usher-search__clear-mobile"
                aria-label="Clear"
                title="Clear"
                @click="clearSearch"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <label class="usher-search__sort">
              <span class="usher-search__sort-label">Filter by</span>
              <select v-model="sortMode" class="usher-search__sort-select" @change="applyLocalFilters">
                <option value="alphabetical">Alphabetical</option>
                <option value="checkout_time">Check Out Time</option>
              </select>
            </label>
            <button
              type="button"
              class="usher-search__button usher-search__button--secondary"
              aria-label="Clear"
              title="Clear"
              @click="clearSearch"
            >
              <CIcon name="cil-x-circle" height="18" />
            </button>
          </form>
        </div>
      </section>

      <section class="usher-results">
        <div v-if="isLoadingInitial" class="usher-results__state">
          <b-spinner variant="primary" label="Loading queue" />
        </div>

        <div v-else-if="items.length === 0" class="usher-results__state">
          No queued child found.
        </div>

        <div v-else class="usher-grid">
          <article
            v-for="item in items"
            :key="item.id"
            class="usher-card"
          >
            <div class="usher-card__top">
              <img
                :src="getPhoto(item.get_photo)"
                :alt="item.name"
                class="usher-card__avatar"
              >

              <div class="usher-card__main">
                <div class="usher-card__headline">
                  <h3 class="usher-card__name">{{ item.name }}</h3>
                  <span
                    class="usher-card__class"
                    :style="classBadgeStyle(item.class_name)"
                  >
                    {{ item.class_name || '-' }}
                  </span>
                </div>

              </div>
            </div>

            <button
              type="button"
              class="usher-card__action"
              :disabled="isActionDisabled(item.id)"
              @click="openCheckOut(item.id)"
            >
              {{ activeActionKidId === item.id ? 'Loading...' : 'Check Out' }}
            </button>
          </article>
        </div>
      </section>
    </div>

    <alertmodal />
    <div v-if="showOperatorPrompt" style="position:fixed;inset:0;background:rgba(19,24,32,.55);display:flex;align-items:center;justify-content:center;padding:20px;z-index:9999;">
      <div style="width:min(460px,100%);background:#fff;border-radius:20px;padding:24px;box-shadow:0 24px 80px rgba(17,24,39,.22);">
        <h3 style="margin:0 0 8px;font-size:24px;font-weight:800;color:#1f2937;">Enter Name</h3>
        <p style="margin:0 0 16px;color:#6b7280;">Name saved 1 day. Used for history log on this page.</p>
        <input
          v-model.trim="operatorDraft"
          type="text"
          class="usher-search__input"
          :style="operatorError ? 'border-color:#dc2626;box-shadow:0 0 0 3px rgba(220,38,38,.12);' : ''"
          placeholder="name"
          @keyup.enter.prevent="saveOperatorName"
        >
        <p v-if="operatorError" style="margin:10px 0 0;color:#dc2626;font-size:13px;font-weight:700;">
          {{ operatorError }}
        </p>
        <div style="display:flex;justify-content:flex-end;gap:12px;margin-top:16px;">
          <button type="button" class="usher-search__button" @click="saveOperatorName">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Pusher from 'pusher-js'
import EventBus from '@/eventBus'
import alertmodal from '@/views/alert/modal.vue'
import ikid from '@/services/ikid'

const CLASS_PALETTE = [
  { bg: '#fff1c7', text: '#8a6216' },
  { bg: '#dff4ff', text: '#0f5f9d' },
  { bg: '#ddf7e7', text: '#217247' },
  { bg: '#ffe3df', text: '#a44b3f' },
  { bg: '#efe6ff', text: '#6845a8' },
  { bg: '#e6f1ff', text: '#2b5f95' },
]

export default {
  name: 'UsherPageNew',
  components: {
    alertmodal,
  },
  data() {
    return {
      form: {
        ikid_id: '',
        status: '',
        from: 'portal',
        source_channel: 'portal_kid_queue',
        executed_by: '',
      },
      showMilitary: false,
      rawItems: [],
      items: [],
      meta: {
        total: 0,
        current_page: 1,
        per_page: 200,
        from: 0,
        to: 0,
      },
      per_page: 200,
      searchInput: '',
      submittedKeyword: '',
      sortMode: 'alphabetical',
      is_active: 1,
      ikid_class_name: '',
      checkin_status: 3,
      searchDebounceTimer: null,
      refreshTimer: null,
      refreshQueued: false,
      refreshInFlight: false,
      refreshLastRunAt: 0,
      refreshIntervalMs: 2500,
      pusherClient: null,
      lastEventId: null,
      isLoadingInitial: true,
      activeActionKidId: null,
      operatorName: '',
      operatorDraft: '',
      showOperatorPrompt: false,
      operatorError: '',
    }
  },
  mounted() {
    this.hydrateOperatorName()
    if (this.operatorName) {
      this.loadData()
    } else {
      this.isLoadingInitial = false
      this.showOperatorPrompt = true
    }
    EventBus.$on('TABLE_UPDATE_USHER_PAGE', this.handleTableUpdate)

    this.pusherClient = new Pusher('fbb60f501f4a91119030', {
      cluster: 'ap1',
      encrypted: true,
    })

    const channel = this.pusherClient.subscribe('kid-channel')
    channel.bind('status-change', this.handleStatusChange)
    channel.bind('status-cancel', this.handleStatusCancel)
  },
  beforeDestroy() {
    EventBus.$off('TABLE_UPDATE_USHER_PAGE', this.handleTableUpdate)
    this.clearSearchDebounce()
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    if (this.pusherClient) {
      this.pusherClient.disconnect()
      this.pusherClient = null
    }
  },
  watch: {
    searchInput(value) {
      const keyword = (value || '').trim()
      this.clearSearchDebounce()

      if (keyword === '') {
        this.submittedKeyword = ''
        this.applyLocalFilters()
        return
      }

      this.searchDebounceTimer = window.setTimeout(() => {
        if (keyword === (this.submittedKeyword || '').trim()) return
        this.runSearch(keyword)
      }, 500)
    },
  },
  methods: {
    currentDate() {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const current = new Date()
      return current.getDate() + ' ' + months[current.getMonth()] + ' ' + current.getFullYear()
    },
    operatorStorageKey() {
      return 'ikid_operator_name_kid_queue'
    },
    getOperatorCookie() {
      const prefix = this.operatorStorageKey() + '='
      const cookies = document.cookie ? document.cookie.split('; ') : []
      const match = cookies.find((cookie) => cookie.indexOf(prefix) === 0)
      return match ? decodeURIComponent(match.substring(prefix.length)) : ''
    },
    hydrateOperatorName() {
      const raw = this.getOperatorCookie()
      if (!raw) return

      this.operatorName = raw
      this.operatorDraft = raw
      this.form.executed_by = raw
    },
    openOperatorPrompt() {
      this.operatorDraft = this.operatorName
      this.operatorError = ''
      this.showOperatorPrompt = true
    },
    cancelOperatorPrompt() {
      this.operatorDraft = this.operatorName
      this.operatorError = ''
      this.showOperatorPrompt = false
    },
    saveOperatorName() {
      const name = (this.operatorDraft || '').trim()
      if (!name) {
        this.operatorError = 'Name is required'
        return
      }
      this.operatorError = ''

      document.cookie = `${this.operatorStorageKey()}=${encodeURIComponent(name)}; max-age=86400; path=/`

      this.operatorName = name
      this.form.executed_by = name
      this.showOperatorPrompt = false

      if (!this.rawItems.length) {
        this.isLoadingInitial = true
        this.loadData()
      }
    },
    submitSearch() {
      const keyword = (this.searchInput || '').trim()
      this.clearSearchDebounce()
      this.runSearch(keyword)
    },
    clearSearch() {
      this.clearSearchDebounce()
      this.searchInput = ''
      this.submittedKeyword = ''
      this.applyLocalFilters()
    },
    clearSearchDebounce() {
      if (this.searchDebounceTimer) {
        window.clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = null
      }
    },
    runSearch(keyword) {
      this.submittedKeyword = keyword
      this.applyLocalFilters()
    },
    handleTableUpdate(data) {
      if (data) {
        this.queueRealtimeRefresh()
      }
    },
    handleStatusChange(data) {
      const nowAction = data && data.now_action
      const from = data && data.from
      if (from === 'portal' && (nowAction === 'queued' || nowAction === 'check out')) {
        this.queueRealtimeRefresh()
      }
    },
    handleStatusCancel() {
      this.queueRealtimeRefresh()
    },
    openCheckOut(uuid) {
      if (this.activeActionKidId) return
      if (!this.operatorName) {
        this.openOperatorPrompt()
        return
      }

      this.activeActionKidId = uuid
      this.form.ikid_id = uuid
      this.form.status = 4
      this.form.executed_by = this.operatorName

      ikid.changeStatusKidQueue(this.form)
        .then(() => {
          this.rawItems = this.rawItems.filter((item) => item.id !== uuid)
          this.applyLocalFilters()
          EventBus.$emit('TABLE_UPDATE_USHER_PAGE', true)
        }).catch((err) => {
          this.showError(err, 'Failed to check out kid')
        }).finally(() => {
          this.activeActionKidId = null
        })
    },
    loadData() {
      const params = {
        if_modified_since_event_id: this.lastEventId,
        limit: this.per_page,
      }

      return ikid.getIkidQueue(params)
        .then((res) => {
          if (res.not_changed) {
            this.lastEventId = res.event_id || this.lastEventId
            this.applyLocalFilters()
            return
          }
          this.lastEventId = res.event_id || this.lastEventId
          this.rawItems = Array.isArray(res.data) ? res.data : []
          this.applyLocalFilters()
        }).catch((err) => {
          this.showError(err, 'Failed to load usher queue')
        }).finally(() => {
          this.isLoadingInitial = false
        })
    },
    applyLocalFilters() {
      const query = (this.submittedKeyword || '').toLowerCase()
      let filtered = Array.isArray(this.rawItems) ? [...this.rawItems] : []

      filtered = filtered.filter((item) => item.checkin_status && item.checkin_status.action === 'queued')

      if (query !== '') {
        filtered = filtered.filter((item) => (item.name || '').toLowerCase().includes(query))
      }

      if (this.sortMode === 'alphabetical') {
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } else if (this.sortMode === 'checkout_time') {
        const toTime = (value) => {
          const time = value ? new Date(String(value).replace(' ', 'T')).getTime() : NaN
          return Number.isNaN(time) ? Infinity : time
        }
        filtered.sort((a, b) => {
          const timeA = toTime(a && a.checkin_time)
          const timeB = toTime(b && b.checkin_time)
          if (timeA === timeB) return 0
          return timeA < timeB ? -1 : 1
        })
      }

      this.items = filtered
      this.meta = {
        total: filtered.length,
        current_page: 1,
        per_page: this.per_page,
        from: filtered.length > 0 ? 1 : 0,
        to: filtered.length,
        is_active: this.is_active,
        ikid_class_name: this.ikid_class_name,
        checkin_status: this.checkin_status,
      }
    },
    queueRealtimeRefresh() {
      this.refreshQueued = true
      this.flushRealtimeRefresh()
    },
    flushRealtimeRefresh() {
      if (!this.refreshQueued || this.refreshInFlight || this.refreshTimer) return
      const elapsed = Date.now() - this.refreshLastRunAt
      const waitMs = Math.max(0, this.refreshIntervalMs - elapsed)

      this.refreshTimer = setTimeout(async () => {
        this.refreshTimer = null
        if (!this.refreshQueued || this.refreshInFlight) return
        this.refreshQueued = false
        this.refreshInFlight = true
        this.refreshLastRunAt = Date.now()
        try {
          await this.loadData()
        } finally {
          this.refreshInFlight = false
          if (this.refreshQueued) this.flushRealtimeRefresh()
        }
      }, waitMs)
    },
    getPhoto(data) {
      if (data && data.filename) {
        return axios.defaults.baseURL + '/getfile/memberphoto/' + data.filename
      }
      return axios.defaults.baseURL + '/getfile/memberphoto/no_photo.jpg'
    },
    classBadgeStyle(className) {
      const label = className || ''
      let sum = 0
      for (let i = 0; i < label.length; i++) {
        sum += label.charCodeAt(i)
      }
      const tone = CLASS_PALETTE[sum % CLASS_PALETTE.length]
      return {
        backgroundColor: tone.bg,
        color: tone.text,
      }
    },
    isActionDisabled(kidId) {
      return !!this.activeActionKidId && this.activeActionKidId !== kidId
    },
    showError(err, fallbackMessage) {
      const message = err && err.response && err.response.data && err.response.data.message
        ? err.response.data.message
        : fallbackMessage
      EventBus.$emit('OPEN_MODAL_ALERT', {
        title: 'Error',
        mode: 'danger',
        data: message,
      })
    },
  },
}
</script>

<style scoped>
.usher-page-new {
  height: 100%;
  padding: 0 !important;
  background:
    radial-gradient(circle at top, rgba(47, 109, 201, 0.12), transparent 35%),
    linear-gradient(180deg, #f7fbff 0%, #eef4fb 100%);
}

.usher-page-new__shell {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.usher-hero {
  position: sticky;
  top: 0;
  z-index: 5;
  flex: 0 0 auto;
  padding: 14px 14px 16px;
  background:
    linear-gradient(180deg, rgba(247, 251, 255, 0.98) 0%, rgba(238, 244, 251, 0.94) 100%);
  border-bottom: 1px solid rgba(199, 215, 236, 0.8);
}

.usher-hero__inner {
  max-width: 1180px;
  margin: 0 auto;
}

.usher-hero__meta {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 12px;
}

.usher-hero__eyebrow {
  margin: 0 0 4px;
  color: #68819d;
  font-size: 12px;
  font-weight: 600;
}

.usher-hero__title {
  margin: 0;
  color: #16375d;
  font-size: 24px;
  font-weight: 700;
}

.usher-hero__total {
  color: #16375d;
  font-size: 22px;
  font-weight: 700;
  white-space: nowrap;
}

.usher-search {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 760px;
}

.usher-search__field {
  position: relative;
  flex: 1;
  min-width: 0;
}

.usher-search__input {
  width: 100%;
  min-height: 42px;
  padding: 0 16px;
  border: 1px solid #c7d7ec;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 18px 40px rgba(20, 63, 108, 0.08);
  color: #17324d;
  font-size: 14px;
  outline: none;
}

.usher-search__input::placeholder {
  color: #9aa9bb;
}

.usher-search__clear-mobile {
  display: none;
}

.usher-search__button {
  min-width: 56px;
  min-height: 50px;
  border: none;
  border-radius: 999px;
  background: #1367b4;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.usher-search__button:hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

.usher-search__button--secondary {
  background: #fff;
  border: 1px solid #c7d7ec;
  color: #315777;
}

.usher-search__sort {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.usher-search__sort-label {
  color: #6c8198;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
}

.usher-search__sort-select {
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid #c7d7ec;
  border-radius: 999px;
  background: #fff;
  color: #17324d;
  font-size: 14px;
  outline: none;
}

.usher-results {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  max-width: 1180px;
  margin: 0 auto;
  width: 100%;
  padding: 18px 14px 24px;
}

.usher-results__state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
  border: 1px dashed #c9d6e6;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.72);
  color: #5f7691;
  font-size: 18px;
}

.usher-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.usher-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px 22px 20px;
  border: 1px solid #cfdae8;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 18px 36px rgba(18, 57, 99, 0.08);
}

.usher-card__top {
  display: flex;
  align-items: center;
  gap: 16px;
}

.usher-card__avatar {
  width: 76px;
  height: 76px;
  border: 4px solid #eef3f8;
  border-radius: 50%;
  object-fit: cover;
  background: #f5f8fc;
  flex: 0 0 auto;
}

.usher-card__main {
  flex: 1;
  min-width: 0;
}

.usher-card__headline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.usher-card__name {
  margin: 0;
  color: #182433;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}

.usher-card__class {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.usher-card__action {
  width: 100%;
  min-height: 48px;
  border: none;
  border-radius: 999px;
  background: #d34545;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.usher-card__action:hover:not(:disabled) {
  opacity: 0.93;
  transform: translateY(-1px);
}

.usher-card__action:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (max-width: 768px) {
  .usher-hero {
    padding: 12px 12px 14px;
  }

  .usher-hero__meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .usher-hero__total {
    font-size: 20px;
  }

  .usher-search {
    flex-direction: row;
    align-items: stretch;
  }

  .usher-search__input {
    min-width: 0;
    min-height: 42px;
    font-size: 16px;
    padding: 0 46px 0 18px;
  }

  .usher-search__sort-label {
    display: none;
  }

  .usher-search__clear-mobile {
    position: absolute;
    top: 50%;
    right: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid #d4dfec;
    border-radius: 50%;
    background: #f3f7fb;
    color: #41627f;
    font-size: 20px;
    font-weight: 700;
    transform: translateY(-50%);
    cursor: pointer;
  }

  .usher-search__button {
    min-width: 54px;
    min-height: 54px;
    flex: 0 0 auto;
  }

  .usher-search__button--secondary {
    display: none;
  }

  .usher-results {
    padding: 16px 12px 20px;
  }

  .usher-grid {
    grid-template-columns: 1fr;
  }

  .usher-card {
    width: 100%;
  }

  .usher-card__headline {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
